"use client";

import { useCallback, useEffect, useRef } from "react";
import type { WorkerResponse, WorkerRequest } from "./types";
import {
  type EcommerceEventParams,
  EcommerceEventParamsSchema,
} from "../e-commerce-event";
import { TrackingEvent } from "@/tracking/types";
import { EcommerceData } from "../get-ecommerce-data";
import {
  ViewEcommerceItemEventSchema,
  ViewEcommerceItemListEventSchema,
  ViewPromotionEventSchema,
  SelectItemEventSchema,
  SelectPromotionEventSchema,
  AddToCartEventSchema,
  RemoveFromCartEventSchema,
  ViewCartEventSchema,
  BeginCheckoutEventSchema,
  AddShippingInfoEventSchema,
  AddPaymentInfoEventSchema,
  PurchaseEventSchema,
} from "../events-schemas";
import { MissingEcommerceEventHandlerError } from "./error";
import { getEcommerceFunnel } from "../funnel";
import Logger from "@cappelendamm/logger";
import { applyItemEventData, calculateItemValue } from "./item-event-data";

export function isEcommerceEvent(
  event: TrackingEvent,
): event is EcommerceEventParams {
  return EcommerceEventParamsSchema.safeParse(event).success;
}

export function useEcommerceTrackEvent({
  onEvent,
}: {
  onEvent: (event: TrackingEvent) => void;
}) {
  const workerRef = useRef<Worker>(null);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL("./tracking.worker.ts", import.meta.url),
      {
        type: "module",
        credentials: "include",
        name: "e-commerce-data-loader",
      },
    );
    workerRef.current.onmessage = (event: MessageEvent<WorkerResponse>) => {
      try {
        const { ecommerceData, eventParams } = event.data;
        const ecommerceEvent: TrackingEvent = mapEcommerceDataToEvent(
          eventParams,
          ecommerceData,
        );
        const funnel = getEcommerceFunnel(eventParams, ecommerceData.items);
        onEvent(funnel ? { ...ecommerceEvent, funnel } : ecommerceEvent);
      } catch (e) {
        if (e instanceof MissingEcommerceEventHandlerError) {
          Logger.error(
            `Unsupported e-commerce event! Event parameters:\n${JSON.stringify(
              event,
              null,
              2,
            )}`,
          );
        } else {
          Logger.error(e);
        }
      }
    };
    return () => {
      // workerRef.current?.terminate();
    };
  }, [onEvent]);

  const trackEvent = useCallback((eventParams: EcommerceEventParams) => {
    const payload: WorkerRequest = {
      eventParams,
      baseUrl: window.location.origin,
    };
    workerRef.current?.postMessage(payload);
  }, []);

  return trackEvent;
}

function mapEcommerceDataToEvent(
  eventParams: EcommerceEventParams,
  ecommerceData: EcommerceData,
) {
  switch (eventParams.eventName) {
    case "add_payment_info":
    case "subscription_add_payment_info":
      return AddPaymentInfoEventSchema.parse({
        eventName: "add_payment_info",
        ecommerce: {
          ...ecommerceData,
          payment_type: eventParams.payment_type,
        },
      });
    case "subscription_add_shipping_info":
    case "add_shipping_info":
      return AddShippingInfoEventSchema.parse({
        eventName: "add_shipping_info",
        ecommerce: {
          ...ecommerceData,
          shipping_tier: eventParams.shipping_tier,
        },
      });
    case "add_to_cart": {
      const addedItems = applyItemEventData(ecommerceData, eventParams);
      return AddToCartEventSchema.parse({
        eventName: "add_to_cart",
        ecommerce: {
          cart_id: eventParams.cartId,
          currency: ecommerceData.currency,
          value: calculateItemValue(addedItems),
          coupon: eventParams.coupon,
          items: addedItems,
        },
      });
    }
    case "subscription_begin_checkout":
    case "begin_checkout":
      return BeginCheckoutEventSchema.parse({
        eventName: "begin_checkout",
        ecommerce: ecommerceData,
      });
    case "subscription_purchase":
      return PurchaseEventSchema.parse({
        eventName: "purchase",
        ecommerce: { ...ecommerceData, transaction_id: eventParams.contractId },
      });
    case "purchase": {
      return PurchaseEventSchema.parse({
        eventName: "purchase",
        ecommerce: { ...ecommerceData, transaction_id: eventParams.cartId },
      });
    }
    case "remove_from_cart": {
      const removedItems = applyItemEventData(ecommerceData, eventParams);
      return RemoveFromCartEventSchema.parse({
        eventName: "remove_from_cart",
        ecommerce: {
          cart_id: eventParams.cartId,
          currency: ecommerceData.currency,
          value: calculateItemValue(removedItems),
          coupon: eventParams.coupon,
          items: removedItems,
        },
      });
    }
    case "select_item":
      return SelectItemEventSchema.parse({
        eventName: "select_item",
        ecommerce: {
          items: ecommerceData.items,
          item_list_id: eventParams.item_list_id,
          item_list_name: eventParams.item_list_name,
        },
      });
    case "select_promotion":
      return SelectPromotionEventSchema.parse({
        eventName: "select_promotion",
        ecommerce: {
          items: ecommerceData.items,
          promotion_id: eventParams.promotion_id,
          promotion_name: eventParams.promotion_name,
          creative_name: eventParams.creative_name,
          creative_slot: eventParams.creative_slot,
        },
      });
    case "view_cart":
      return ViewCartEventSchema.parse({
        eventName: "view_cart",
        ecommerce: ecommerceData,
      });
    case "view_item":
      return ViewEcommerceItemEventSchema.parse({
        eventName: "view_item",
        ecommerce: {
          currency: ecommerceData.currency,
          value: ecommerceData.value ?? 0,
          items: ecommerceData.items,
        },
      });
    case "view_item_list":
      return ViewEcommerceItemListEventSchema.parse({
        eventName: "view_item_list",
        ecommerce: {
          currency: ecommerceData.currency,
          items: ecommerceData.items,
          item_list_id: eventParams.item_list_id,
          item_list_name: eventParams.item_list_name,
        },
      });
    case "view_promotion":
      return ViewPromotionEventSchema.parse({
        eventName: "view_promotion",
        ecommerce: {
          items: ecommerceData.items,
          promotion_id: eventParams.promotion_id,
          promotion_name: eventParams.promotion_name,
          creative_name: eventParams.creative_name,
          creative_slot: eventParams.creative_slot,
        },
      });
    default: {
      throw new MissingEcommerceEventHandlerError();
    }
  }
}
